import config from '../../config/config';
import { PortalType } from '../../config/enum';
import { detectPortalType } from '../../contexts/PortalContext/PortalProvider';

import deleteRequest from './base/delete';
import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

const portalType = detectPortalType();

export default {
  /**
   * Fetch tariffs list
   * @param {any} query - The query
   */
  fetchTariffs: (query: QueryParameters = {}): Promise<any> => {
    const parameters = {
      ...query,
      limit: query?.limit || 10,
    };

    return getRequest(
      `${config.tariffApiUrl}api/${portalType === PortalType.PARTNER ? 'partner' : 'cpo'}/v1/tariffs`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch single tariff
   * @param {any} id - The id
   */
  fetchSingleTariff: (id: string, isPartner = false): Promise<any> => {
    return getRequest(
      `${config.tariffApiUrl}api/${isPartner ? 'partner' : 'cpo'}/v1/tariffs/${id}`,
      {},
    );
  },

  /**
   * Fetch tariff filters
   */
  fetchTariffFilters: (): Promise<any> => {
    return getRequest(
      `${config.tariffApiUrl}api/${portalType === PortalType.PARTNER ? 'partner' : 'cpo'}/v1/tariffs/filters`,
    );
  },

  /**
   * Export tariff data
   * @param {any} query - The query
   * @returns {Promise<any>} A promise that resolves with the exported data
   */
  exportTariffData: (query: QueryParameters = {}): Promise<any> => {
    const parameters = {
      ...query,
    };
    const isPartner = !!(query.partner || query.isPartner);
    return getRequest(
      `${config.tariffApiUrl}api/${isPartner || portalType === PortalType.PARTNER ? 'partner' : 'cpo'}/v1/tariffs/export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Create tariff
   * @param {any} data - The data
   */
  createTariff: (data: any): Promise<any> => {
    const parameters = { ...data };

    return postRequest(
      `${config.tariffApiUrl}api/${portalType === PortalType.PARTNER ? 'partner' : 'cpo'}/v1/tariffs`,
      parameters,
    );
  },

  /**
   * Edit tariff
   * @param {string} id - The id
   * @param {any} data - The data
   * @param {boolean} [isPartner] - Flag indicating if it is a partner tariff
   * @returns {Promise<any>} A promise that resolves with the response
   */
  editTariff: (id: string, data: any, isPartner = false): Promise<any> => {
    const parameters = { ...data };

    return patchRequest(
      `${config.tariffApiUrl}api/${isPartner || portalType === PortalType.PARTNER ? 'partner' : 'cpo'}/v1/tariffs/${id}`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Edit tariff availability
   * @param {string} id - The id
   * @param {any} data - The data
   * @param {boolean} [isPartner] - Flag indicating if it is a partner tariff
   * @returns {Promise<any>} A promise that resolves with the response
   */
  editAvailabilityTariff: (id: string, data: any, isPartner = false): Promise<any> => {
    const parameters = { ...data };

    return patchRequest(
      `${config.tariffApiUrl}api/${isPartner || portalType === PortalType.PARTNER ? 'partner' : 'cpo'}/v1/tariffs/${id}/status`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Delete tariff
   * @param {string} id - The id
   * @param {boolean} [isPartner] - Flag indicating if it is a partner tariff
   * @returns {Promise<any>} A promise that resolves with the response
   */
  deleteTariff: (id: string, isPartner = false): Promise<any> => {
    return deleteRequest(
      `${config.tariffApiUrl}api/${isPartner || portalType === PortalType.PARTNER ? 'partner' : 'cpo'}/v1/tariffs/${id}`,
    );
  },

  /**
   * Fetch one tariff
   * @param {any} id - The id
   */
  fetchOneTariff: (id: string): Promise<any> => {
    return getRequest(
      `${config.tariffApiUrl}api/${portalType === PortalType.PARTNER ? 'partner' : 'cpo'}/v1/tariffs/${id}`,
      {},
    );
  },

  /**
   * Fetch tariff counts
   * @param {any} type - The type
   */
  fetchTariffCounts: (type: string): Promise<any> => {
    return getRequest(
      `${config.tariffApiUrl}api/${portalType === PortalType.PARTNER ? 'partner' : 'cpo'}/v1/tariffs/status-count`,
      { type },
    );
  },

  /**
   * Fetch tariff calculator data
   * @param {any} data - The data
   */
  fetchTariffCalculatorData: (data: any): Promise<any> => {
    return postRequest(
      `${config.tariffApiUrl}api/${portalType === PortalType.PARTNER ? 'partner' : 'cpo'}/v1/tariffs/calculate`,
      data,
    );
  },

  /**
   * Fetch tariff calculator data
   * @param {string} tariffId - The tariffId
   * @param {any} data - The data
   * @param {boolean} [isPartner] - Flag indicating if it is a partner tariff
   * @returns {Promise<any>} A promise that resolves with the calculator data
   */
  fetchSingerTariffCalculatorData: (tariffId: string, data: any, isPartner = false): Promise<any> => {
    return postRequest(
      `${config.tariffApiUrl}api/${isPartner || portalType === PortalType.PARTNER ? 'partner' : 'cpo'}/v1/tariffs/${tariffId}/calculate`,
      data,
    );
  },

  /**
   * Fetch tariff calculator data
   * @param {any} tariffId - The tariffId
   */
  syncAvailabilityStatus: (tariffId: string): Promise<any> => {
    return getRequest(
      `${config.tariffApiUrl}api/${portalType === PortalType.PARTNER ? 'partner' : 'cpo'}/v1/tariffs/${tariffId}/sync-availability-status`,
      {},
    );
  },

  /**
   * Manually syncs tariff data with the OCPI gateway.
   * @returns {any} A promise that resolves with the sync response.
   */
  syncOcpiTariffs: (): Promise<any> => {
    return postRequest(`${config.tariffApiUrl}api/ocpi/v1/tariffs/sync`, {});
  },
};
